/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { GenericCard } from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { mapDatacenterDetailToSections } from "kontrolEdge/constants/constants";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import { getTextColor } from "kontrolEdge/helpers/getTextColor";
import {
  DatacenterDetailSection,
  SectionTable,
  SubnetRow,
} from "./DatacenterDetailsData";
import { useMetaEditor } from "kontrolEdge/components/Shared/NameTagEdit/useMetaEditor";
import { EditableTagList } from "kontrolEdge/components/Shared/NameTagEdit/EditableTagList";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { usePhoneFold } from "kontrolEdge/components/Shared/DetailSummary/usePhoneFold";

interface Props {
  data: DatacenterDetailSection;
  tenantId: number;
  /** Pre-hydrated tag list from the detail GET inline `tags` field (§5.5). */
  initialAssignedTags?: ResourceTagSummary[];
  /** Tenant the resource actually lives in. See PortDetails.tsx for rationale. */
  resourceTenantId?: number;
}

const DataCenterDetailsCard: React.FC<Props> = ({ data, initialAssignedTags, resourceTenantId }) => {
  const navigate = useNavigate();
  const api = useAxiosAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  //Port id selected from MainPort
  const [searchParams] = useSearchParams();
  const datacenterId = searchParams.get("datacenterId");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rawConnectionData, setRawConnectionData] = useState<any>(null);
  const [connectionDetailsData, setConnectionDetailsData] = useState<
    DatacenterDetailSection[]
  >([]);
  const { performanceData } = useAppSelector((state) => state.datacenter);
  const shouldCollapse = data.isCollapsible ?? true;
  // Folded on a phone, with a one-line summary (CTX-1421).
  const fold = usePhoneFold(data, { isCollapsible: shouldCollapse, initialExpanded: data.initialExpanded });

  // Logic for Name and Tag editing
  const nameSeed = String(
    data.items.find((i) => i.label === "Data Center Name")?.value ?? ""
  );
  const tagSeed = (() => {
    const v = data.items.find((i) => i.label === "Tag")?.value;
    return Array.isArray(v) ? v.join(", ") : String(v ?? "");
  })();

  const meta = useMetaEditor({
    api,
    kind: "datacenter",
    resourceId: datacenterId,
    tenantId,
    initialName: nameSeed,
    initialTag: tagSeed,
  });

  // Convert tag string to array for EditableTagList
  const tagArray = useMemo(() => {
    if (!meta.tagValue || meta.tagValue.trim() === "") return [];
    return meta.tagValue.split(",").map(t => t.trim()).filter(t => t !== "");
  }, [meta.tagValue]);

  // Update connectionDetailsData when performanceData changes (and rawConnectionData exists)
  useEffect(() => {
    if (rawConnectionData && performanceData) {
      setConnectionDetailsData(
        mapDatacenterDetailToSections(rawConnectionData, performanceData)
      );
    }
  }, [rawConnectionData, performanceData]);

  return (
    <>
      <GenericCard
        {...fold}
        bodyContent={
          <div className="space-y-2">
            {data.items.map((item, idx) => {
              if (item.type === "subtitle") {
                return (
                  <div key={idx} className="pt-3 pb-1">
                    <h4 className="text-sm font-medium text-semantic-text-secondary">
                      {item.label}
                    </h4>
                  </div>
                );
              }
              if (item.label === "Data Center Name") {
                return (
                  <div
                    key={idx}
                    className="flex justify-between w-full py-1 border-b"
                  >
                    <h3 className="text-sm text-semantic-text-primary">
                      Datacenter Name
                    </h3>
                    <div className="flex items-center gap-2">
                      <GenericTag
                        label={meta.nameValue || "—"}
                        variant="neutral"
                        size="small"
                        minWidth="100px"
                        className="text-center"
                        editable={true}
                        value={meta.nameValue}
                        onSave={(newValue) => {
                          meta.saveName(newValue);
                          meta.setNameValue(newValue);
                        }}
                        onCancel={() => {
                          meta.cancelName();
                        }}
                        isSaving={meta.savingField === "name"}
                        disabled={meta.savingField !== null}
                      />
                    </div>
                  </div>
                );
              }
              if (item.label === "Tag") {
                return (
                  <div
                    key={idx}
                    className="flex justify-between w-full py-1 border-b"
                  >
                    <h3 className="text-sm text-semantic-text-primary">Tag</h3>
                    <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                      <EditableTagList
                        tags={tagArray}
                        onUpdate={meta.updateTagArray}
                        disabled={meta.savingField !== null}
                        isLoading={meta.isSavingTag}
                        resourceType="ke-dci"
                        resourceId={datacenterId}
                        initialAssignedTags={initialAssignedTags}
                        tenantId={resourceTenantId}
                      />
                    </div>
                  </div>
                );
              }
              // Default: render a non-editable key-value row
              return (
                <div
                  key={idx}
                  className={`${item.statusVariant ? "hidden sm:flex" : "flex"} justify-between w-full py-1 ${
                    idx !== data.items.length - 1 ? "border-b" : ""
                  }`}
                >
                  <h3 className="text-sm text-semantic-text-primary">
                    {item.label}
                  </h3>
                  {item.statusVariant ? (
                    <ServiceStatusValue
                      label={String(item.value ?? "-")}
                      variant={item.statusVariant}
                      tooltip={item.tooltip}
                    />
                  ) : item.type === "tag" ? (
                    <GenericTag label={String(item.value)} variant="success" />
                  ) : item.tooltip ? (
                    <GenericTooltip
                      text={item.tooltip}
                      position="bottom"
                      horizontalAlign="right"
                      width="20rem"
                    >
                      <span
                        className={`text-sm ${getTextColor(
                          item.label,
                          String(item.value || "")
                        )}`}
                      >
                        {item.value || "-"}
                      </span>
                    </GenericTooltip>
                  ) : (
                    <p
                      className={`text-sm ${getTextColor(
                        item.label,
                        String(item.value || "")
                      )}`}
                    >
                      {item.value || "-"}
                    </p>
                  )}
                </div>
              );
            })}
            {data.table && (
              <GenericTable
                mobileCards
                columns={(() => {
                  // Check if this is the "Data Center Details" section with ports table
                  if (data.header === "Data Center Details" && data.table) {
                    const baseColumns = (data.table as SectionTable<any>)
                      .columns;
                    // Override the "Port" column to make it clickeable
                    return baseColumns.map((col) => {
                      if (col.header === "Port") {
                        return {
                          ...col,
                          renderCell: (row: {
                            id?: number;
                            portName?: string;
                            subscriptionId?: string;
                            kind?: "port" | "sdwan_hub" | "fwaas";
                          }) => {
                            const portId = row.id;
                            const portName =
                              row.portName || row.subscriptionId || "-";

                            // Route by endpoint kind: SD-WAN hub -> hub
                            // details, port (or legacy) -> port details.
                            // FWAAS has no per-id detail route -> plain text.
                            const endpointPath =
                              portId != null && portName !== "-"
                                ? row.kind === "sdwan_hub"
                                  ? buildDetailPath("sdwanHub", { sdwanId: portId })
                                  : !row.kind || row.kind === "port"
                                    ? buildDetailPath("port", { portId })
                                    : null
                                : null;
                            const onClick = endpointPath ? () => navigate(endpointPath) : null;

                            if (onClick) {
                              return (
                                <button
                                  {...viewHref(endpointPath)}
                                  onClick={onClick}
                                  className="
                                    hover:text-semantic-text-brand
                                    hover:underline
                                    focus:underline
                                    active:underline
                                    font-medium
                                    underline
                                    text-semantic-text-brand
                                  "
                                >
                                  {portName}
                                </button>
                              );
                            }
                            return <span>{portName}</span>;
                          },
                        } as GenericTableColumn<any>;
                      }
                      return col;
                    });
                  }
                  // For other tables, return columns as-is
                  return (data.table as SectionTable<SubnetRow>).columns;
                })()}
                data={(data.table as SectionTable<any>).data}
                tableClassName="w-full"
                isFooterVisible={false}
              />
            )}
          </div>
        }
      />
    </>
  );
};

export default DataCenterDetailsCard;
